import type { MeResponse } from '@an-crm/shared';
import { Link } from '@tanstack/react-router';
import { CircleUser, LogOut, Monitor, Moon, Sun } from 'lucide-react';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { setThemePreference, type ThemePreference, useTheme } from '@/lib/theme';

function prettyRole(role: string): string {
  return role.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function UserMenu({ me, onSignOut }: { me: MeResponse; onSignOut: () => void }) {
  const { preference } = useTheme();
  const roles = me.user.isPlatformAdmin && me.roles.length === 0 ? ['Super Admin'] : me.roles.map(prettyRole);
  const supportMode = Boolean(me.support);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="focus-ring flex items-center gap-2 rounded-full p-0.5 transition-shadow hover:ring-2 hover:ring-border">
        {/* the accessible name starts with the visible initials, then explains the control */}
        <Avatar name={me.user.name} className="size-8 text-sm" />
        <span className="sr-only">Account menu for {me.user.name}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <div className="flex items-center gap-3 px-2.5 py-2">
          <Avatar name={me.user.name} className="size-10 text-sm" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-fg">{me.user.name}</p>
            <p className="truncate text-xs text-fg-muted">{me.user.email}</p>
          </div>
        </div>
        {roles.length > 0 && (
          <div className="flex flex-wrap gap-1 px-2.5 pb-2">
            {roles.map((r) => (
              <Badge key={r} tone="primary">
                {r}
              </Badge>
            ))}
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/account">
            <CircleUser /> Account &amp; security
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={preference}
          onValueChange={(v) => setThemePreference(v as ThemePreference)}
        >
          <DropdownMenuRadioItem value="light">
            <Sun /> Light
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon /> Dark
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">
            <Monitor /> System
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onSignOut} className="text-danger [&_svg]:text-danger">
          <LogOut /> {supportMode ? 'End support session' : 'Sign out'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
